// Procedural gift sound effects via Web Audio API.
// CRITICAL: must unlockAudio() be called from a user-gesture handler (e.g. button click)
// BEFORE any await/async boundary. iOS/Safari blocks AudioContext until then.

let ctx = null;
let unlocked = false;

function getCtx() {
  if (!ctx) {
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch { return null; }
  }
  return ctx;
}

// Call this SYNCHRONOUSLY inside a click/tap handler so iOS allows audio.
export function unlockAudio() {
  const c = getCtx(); if (!c) return;
  if (c.state === "suspended") { try { c.resume(); } catch {} }
  if (!unlocked) {
    // Play a near-silent click to satisfy iOS gesture requirement
    try {
      const t0 = c.currentTime;
      const osc = c.createOscillator();
      const g = c.createGain();
      g.gain.setValueAtTime(0.001, t0);
      osc.connect(g); g.connect(c.destination);
      osc.start(t0); osc.stop(t0 + 0.01);
      unlocked = true;
    } catch {}
  }
}

function tone(freq, dur, type = "sine", gain = 0.35, delay = 0) {
  const c = getCtx(); if (!c) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(c.destination);
  osc.start(t0); osc.stop(t0 + dur + 0.05);
}

function sweep(f1, f2, dur, type = "sawtooth", gain = 0.28, delay = 0) {
  const c = getCtx(); if (!c) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f1, t0);
  osc.frequency.exponentialRampToValueAtTime(f2, t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.03);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(c.destination);
  osc.start(t0); osc.stop(t0 + dur + 0.05);
}

export function playGiftSound(gift) {
  const c = getCtx(); if (!c) return;
  if (c.state === "suspended") { try { c.resume(); } catch {} }
  const name = (gift?.name || "").toLowerCase();
  const price = gift?.price || 1;

  if (/mahadev|om|namah|shiv|jai|lakshmi|ganesh|ganpati|krishna|ram|durga|hanuman|allah|wahe|guru|christ/.test(name)) {
    // Temple bell + chant melody (spiritual)
    [392, 523, 659, 523, 659, 784].forEach((f, i) => tone(f, 0.6, "sine", 0.3, i * 0.22));
    sweep(220, 110, 1.6, "sine", 0.18, 0.05);
    return;
  }
  if (/rose|love|heart|kiss|valentine|cupid/.test(name)) {
    [523, 659, 784, 1046].forEach((f, i) => tone(f, 0.25, "sine", 0.32, i * 0.1));
    return;
  }
  if (/coin|gold|cash|money|diamond|jewel|crown/.test(name)) {
    [880, 1175, 1568, 2093].forEach((f, i) => tone(f, 0.15, "square", 0.22, i * 0.06));
    return;
  }
  if (/rocket|bomb|fire|blast|boom|explode/.test(name)) {
    sweep(80, 1400, 0.35, "sawtooth", 0.35);
    sweep(900, 100, 0.6, "triangle", 0.25, 0.05);
    return;
  }
  if (/king|queen|royal|throne|emperor/.test(name)) {
    [523, 659, 784, 1046, 1318].forEach((f, i) => tone(f, 0.25, "triangle", 0.28, i * 0.12));
    return;
  }
  if (/clap|cheer|applause|hooray|congrats|wow/.test(name)) {
    for (let i = 0; i < 10; i++) tone(2000 + Math.random() * 1500, 0.05, "square", 0.18, i * 0.05);
    return;
  }
  if (price >= 1000) {
    [659, 784, 988, 1175, 1568].forEach((f, i) => tone(f, 0.22, "triangle", 0.3, i * 0.09));
  } else if (price >= 100) {
    [659, 988, 1318].forEach((f, i) => tone(f, 0.22, "sine", 0.3, i * 0.08));
  } else {
    [880, 1175].forEach((f, i) => tone(f, 0.18, "sine", 0.3, i * 0.08));
  }
}
